/**
 * Projections between the production API contract and the UI working model.
 * Array order is the source of truth for priority on save.
 *
 * Ported from the prototype; reads the real `IAdapterPriority` rows returned by
 * settings/api/getPaymentAdapterPriorities and builds the body consumed by
 * settings/api/postAdapterPriorities (array order == priority).
 */

import type { AdapterRow, TrafficType } from './types'

/** Default upper bound when the API omits maxDeposit for a row. */
export const DEFAULT_MAX_DEPOSIT = 1_000_000

/** Raw priority row shape as returned by GET .../priority/list (subset used). */
export interface ApiPriorityRow {
  paymentAdapter: { id: number; name: string; enabled: boolean }
  priority: number
  enabled: boolean
  minDeposit?: number
  maxDeposit?: number
  useTrafficType?: TrafficType
  source?: 'MERCHANT' | 'PLATFORM'
}

/** A single available adapter from GET .../payment-adapter/list. */
export interface ApiAdapterRef {
  id: number | string
  name: string
  enabled?: boolean
  minDeposit?: number
  maxDeposit?: number
  useTrafficType?: TrafficType
}

/** A single item of the POST .../priority/ body. */
export interface PriorityMutationItem {
  paymentAdapterId: number
  enabled: boolean
  priority: number
  minDeposit: number
  maxDeposit: number
  useTrafficType: TrafficType
  source: 'MERCHANT' | 'PLATFORM'
}

/**
 * Convert API priority rows + the full adapter catalogue into ordered UI rows.
 *
 * Mirrors the merge the legacy PaymentAdapterPriorities screen performs: rows
 * present in the priority list keep their settings (sorted by `priority`), then
 * any catalogue adapter not already present is appended as a disabled row so the
 * operator can see and enable the full set.
 */
export function rowsFromApi(
  priorities: ApiPriorityRow[],
  catalogue: ApiAdapterRef[],
  source: 'MERCHANT' | 'PLATFORM'
): AdapterRow[] {
  const out: AdapterRow[] = []

  ;[...priorities]
    .sort((a, b) => a.priority - b.priority)
    .forEach((r) => {
      out.push({
        adapterId: r.paymentAdapter.id,
        name: r.paymentAdapter.name,
        globallyEnabled: r.paymentAdapter.enabled,
        enabled: r.enabled && r.paymentAdapter.enabled,
        minDeposit: r.minDeposit ?? 0,
        maxDeposit: r.maxDeposit ?? DEFAULT_MAX_DEPOSIT,
        useTrafficType: r.useTrafficType ?? 'NONE',
        source: r.source ?? source,
      })
    })

  catalogue.forEach((a) => {
    const id = Number(a.id)
    if (out.some((row) => row.adapterId === id)) return
    out.push({
      adapterId: id,
      name: a.name,
      globallyEnabled: a.enabled ?? true,
      enabled: false,
      minDeposit: a.minDeposit ?? 0,
      maxDeposit: a.maxDeposit ?? DEFAULT_MAX_DEPOSIT,
      useTrafficType: a.useTrafficType ?? 'NONE',
      source,
    })
  })

  return out
}

/**
 * Build the POST priorities array. Array index + 1 == priority (invariant).
 * CHC-873: `source` is the configuration's context axis — applied uniformly
 * from the context argument, NOT read per-row (the per-row column was removed).
 */
export function toPriorities(rows: AdapterRow[], source: 'MERCHANT' | 'PLATFORM'): PriorityMutationItem[] {
  return rows.map((row, index) => ({
    paymentAdapterId: row.adapterId,
    enabled: row.enabled,
    priority: index + 1,
    minDeposit: row.minDeposit || 0,
    maxDeposit: row.maxDeposit || 0,
    useTrafficType: row.useTrafficType || 'NONE',
    source,
  }))
}

/** Build a fresh template row set from the adapter catalogue (empty-fill flow). */
export function templateRows(
  catalogue: ApiAdapterRef[],
  source: 'MERCHANT' | 'PLATFORM'
): AdapterRow[] {
  return catalogue.map((a) => ({
    adapterId: Number(a.id),
    name: a.name,
    globallyEnabled: a.enabled ?? true,
    enabled: a.enabled ?? true,
    minDeposit: a.minDeposit ?? 100,
    maxDeposit: a.maxDeposit ?? DEFAULT_MAX_DEPOSIT,
    useTrafficType: 'NONE' as TrafficType,
    source,
  }))
}
